import React, { useState } from 'react';
import CaseTypeOrganizationUnitForm, {
	CaseTypeOrganizationUnitFormDefaultValues,
	CaseTypeOrganizationUnitFormValues,
} from './CaseTypeOrganizationUnitForm';
import apiClient from '../../../../../Services/ApiClient';
import useApiClient from '../../../../Base/Hooks/useApiClient';
import useTranslation from '../../../../../Hooks/useTranslation';
import useCaseTypes from '../../../Hooks/useCaseTypes';
import { useEffectAsync } from '../../../../../helpers';
import Loading from '../../../../../Components/Loading';

interface Props {
	organizationUnitId?: string;
	onSuccess: () => void;
	workplaceId: string;
	isOnlyOneRoot: boolean;
}

const CaseTypeOrganizationUnit: React.FC<Props> = ({
	organizationUnitId,
	workplaceId,
	onSuccess,
	isOnlyOneRoot,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [defaultValues, setDefaultValues] =
		useState<CaseTypeOrganizationUnitFormDefaultValues>();
	const { displayError, displaySuccess } = useApiClient();
	const { t } = useTranslation();

	const { getDefaultsValuesForOrganizationUnit } = useCaseTypes();

	// GETTING DEFAULT VALUES
	useEffectAsync(async () => {
		if (organizationUnitId) {
			const defaults = await getDefaultsValuesForOrganizationUnit(
				organizationUnitId,
			);
			if (defaults) {
				setDefaultValues(defaults);
			}
		}
		setIsLoading(false);
	}, [organizationUnitId]);

	const createOrganizationUnitData = (
		values: CaseTypeOrganizationUnitFormValues,
	) => {
		return {
			type: 'folder',
			label: values.label,
			parentCaseTypeId: values?.parentCaseTypeId?.value,
		};
	};

	const handleCreate = async (values: CaseTypeOrganizationUnitFormValues) => {
		try {
			const response = await apiClient.post(
				`/workplaces/${workplaceId}/case/type`,
				{
					caseType: createOrganizationUnitData(values),
				},
			);
			displaySuccess(
				t('component.caseType.organizationUnitSuccessfullyCreated') ??
					'Created',
			);
			onSuccess();
		} catch (e: any) {
			displayError(e);
		}
	};

	const handleEdit = async (values: CaseTypeOrganizationUnitFormValues) => {
		try {
			const response = await apiClient.patch(
				`/workplaces/${workplaceId}/case/type/${organizationUnitId}`,
				{
					caseType: createOrganizationUnitData(values),
				},
			);
			if (response.status === 200) {
				displaySuccess(
					t('component.caseType.organizationUnitSuccessfullyEdited') ??
						'Edited',
				);
				onSuccess();
			}
		} catch (e: any) {
			displayError(e);
		}
	};

	const handleRemove = async () => {
		try {
			await apiClient.delete(
				`/workplaces/${workplaceId}/case/type/${organizationUnitId}`,
			);
			displaySuccess(
				t('component.caseType.organizationUnitSuccessfullyDeleted') ??
					'Deleted',
			);
			onSuccess();
		} catch (e: any) {
			displayError(e);
		}
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<CaseTypeOrganizationUnitForm
			onSuccess={organizationUnitId ? handleEdit : handleCreate}
			onRemove={handleRemove}
			workplaceId={workplaceId}
			defaultValues={defaultValues}
			isOnlyOneRoot={isOnlyOneRoot}
			organizationUnitId={organizationUnitId}
		/>
	);
};

export default CaseTypeOrganizationUnit;
